import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { format, addDays, startOfMonth, differenceInDays, addYears } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Loader2, 
  Calendar as CalendarIcon, 
  LinkIcon, 
  ArrowUp,
  ArrowDown,
  RefreshCw,
  PlusCircle,
  AlertCircle,
  Info as InfoIcon,
  CheckCircle,
  XCircle,
  Copy,
  Sparkles as SparklesIcon,
  DollarSign,
  Settings,
  CloudDownload as CloudDownloadIcon,
  Pencil as PencilIcon,
  FileDown,
  FileUp,
  Eye,
  Download
} from 'lucide-react';
import { queryClient } from '@/lib/queryClient';
import { apiRequest } from '@/lib/api';
import ManagerLayout from '@/components/layout/ManagerLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

// Constants for calendar date status
enum CalendarDateStatus {
  AVAILABLE = "available",
  BOOKED = "booked",
  BLOCKED = "blocked",
}

// Constants for calendar data source
enum CalendarSource {
  MANUAL = "manual",
  ICAL = "ical",
  AIRBNB = "airbnb",
  BOOKING = "booking",
  GUESTY = "guesty",
  HOSTAWAY = "hostaway",
}

// Constants for calendar price source
enum CalendarPriceSource {
  MANUAL = "manual",
  AI = "ai",
  IMPORTED = "imported",
}

// Smart Pricing intensity options
enum SmartPricingIntensity {
  MILD = "mild",
  MEDIUM = "medium",
  STRONG = "strong",
}

// Add new enum for pricing mode
enum PricingMode {
  MANUAL = "manual",
  AI = "ai"
}

// Calendar date type
type CalendarDate = {
  id: number;
  propertyId: number;
  date: string;
  status: CalendarDateStatus;
  price: number | null;
  source: CalendarSource;
  priceSource?: CalendarPriceSource;
  sourceEventId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

// Property type
type Property = {
  id: number;
  title: string;
  iCalUrl: string | null;
  useSmartPricing?: boolean;
  lastAiPriceUpdate?: Date | null;
  basePrice?: number;
  area?: string;
  bedrooms?: number;
  bathrooms?: number;
  pricingMode?: PricingMode;
  airbnbListingId?: string;
};

export default function CalendarEnhancedV5() {
  // State variables
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [isRangeSelectionMode, setIsRangeSelectionMode] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isIcalModalOpen, setIsIcalModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportCSVModalOpen, setIsExportCSVModalOpen] = useState(false);
  const [dateStatus, setDateStatus] = useState<CalendarDateStatus>(CalendarDateStatus.AVAILABLE);
  const [datePrice, setDatePrice] = useState('');
  const [dateNotes, setDateNotes] = useState('');
  const [bulkStatus, setBulkStatus] = useState<CalendarDateStatus>(CalendarDateStatus.AVAILABLE);
  const [bulkPrice, setBulkPrice] = useState('');
  const [icalUrl, setIcalUrl] = useState('');
  const [icalSource, setIcalSource] = useState<CalendarSource>(CalendarSource.ICAL);
  const [icalLabel, setIcalLabel] = useState('');
  const [exportUrl, setExportUrl] = useState('');
  const [useSmartPricing, setUseSmartPricing] = useState(false);
  const [useAiPricing, setUseAiPricing] = useState(false);
  const [smartPricingIntensity, setSmartPricingIntensity] = useState<SmartPricingIntensity>(SmartPricingIntensity.MEDIUM);
  const [isGeneratingPrices, setIsGeneratingPrices] = useState(false);
  const [csvStartDate, setCsvStartDate] = useState<Date | undefined>(undefined);
  const [csvEndDate, setCsvEndDate] = useState<Date | undefined>(undefined);
  const [showAiPriceConfirm, setShowAiPriceConfirm] = useState(false);
  const [showManualPriceConfirm, setShowManualPriceConfirm] = useState(false);
  const [isApplyingManualPrice, setIsApplyingManualPrice] = useState(false);
  
  const { toast } = useToast();
  const [, navigate] = useLocation();
  
  // Query to get properties
  const { data: properties, isLoading: propertiesLoading } = useQuery({
    queryKey: ['my-properties'],
    queryFn: () => apiRequest('GET', 'my-properties'),
  });
  
  // Get calendar dates query
  const { data: calendarDates, isLoading: calendarLoading, refetch: refetchCalendar } = useQuery({
    queryKey: ['properties', selectedPropertyId, 'calendar'],
    queryFn: async () => {
      if (!selectedPropertyId) return [];
      
      const startDate = new Date();
      const endDate = addYears(startDate, 1); // Show 1 year
      
      try {
        const data = await apiRequest(
          'GET', 
          `properties/${selectedPropertyId}/calendar?startDate=${format(startDate, 'yyyy-MM-dd')}&endDate=${format(endDate, 'yyyy-MM-dd')}`
        );
        return data.data || [];
      } catch (error: any) {
        console.error('Failed to fetch calendar:', error.message);
        return [];
      }
    },
    enabled: !!selectedPropertyId,
  });
  
  // Get calendar imports query
  const { data: calendarImports, isLoading: importsLoading, refetch: refetchImports } = useQuery({
    queryKey: ['properties', selectedPropertyId, 'calendar-imports'],
    queryFn: async () => {
      if (!selectedPropertyId) return [];
      
      try {
        const data = await apiRequest(
          'GET', 
          `properties/${selectedPropertyId}/calendar-imports`
        );
        return data;
      } catch (error: any) {
        console.error('Failed to fetch calendar imports:', error);
        return [];
      }
    },
    enabled: !!selectedPropertyId,
  });
  
  // When property changes, set property options
  useEffect(() => {
    if (selectedPropertyId && properties) {
      const property = properties.find((p: Property) => p.id === selectedPropertyId);
      if (property) {
        setSelectedProperty(property);
        setUseSmartPricing(!!property.useSmartPricing);
      }
    }
  }, [selectedPropertyId, properties]);
  
 
  // Sync iCal calendar mutation
  const syncIcalMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPropertyId) {
        throw new Error('No property selected');
      }
      
      const response = await apiRequest(
        'POST',
        `properties/${selectedPropertyId}/calendar-imports`,
        { 
          url: icalUrl,
          source: icalSource,
          label: icalLabel || `${icalSource} Calendar`,
        }
      );
      
      return response;
    },
    onSuccess: () => {
      toast({
        title: 'Calendar imported successfully',
        description: 'The external calendar has been imported',
      });
      setIsIcalModalOpen(false);
      setIcalUrl('');
      setIcalLabel('');
      refetchImports();
      refetchCalendar();
    },
    onError: (error: Error) => {
      toast({
        title: 'Import failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  
  // Delete calendar import mutation
  const deleteImportMutation = useMutation({
    mutationFn: async (importId: number) => {
      if (!selectedPropertyId) {
        throw new Error('No property selected');
      }
      
      const response = await apiRequest(
        'DELETE',
        `properties/${selectedPropertyId}/calendar-imports/${importId}`
      );
      
      return response;
    },
    onSuccess: () => {
      toast({
        title: 'Import removed',
        description: 'Calendar import has been removed',
      });
      refetchImports();
      refetchCalendar();
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove import',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  
  // Update calendar date mutation
  const updateDateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPropertyId || !selectedDate) {
        throw new Error('Property and date are required');
      }
      
      const response = await apiRequest(
        'POST',
        `properties/${selectedPropertyId}/calendar`,
        {
          dates: [
            {
              date: format(selectedDate, 'yyyy-MM-dd'),
              status: dateStatus,
              price: datePrice ? parseInt(datePrice) : null,
              notes: dateNotes || null,
            },
          ],
        }
      );
      
      return response;
    },
    onSuccess: () => {
      toast({
        title: 'Calendar updated',
        description: 'The date has been updated successfully',
      });
      setIsDateModalOpen(false);
      setSelectedDate(undefined); // Unselect the day after saving
      refetchCalendar();
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update calendar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  
  // Bulk update calendar mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPropertyId || !dateRange.from || !dateRange.to) {
        throw new Error('Property and date range are required');
      }
      const startDate = dateRange.from;
      const endDate = dateRange.to;
      const totalDays = differenceInDays(endDate, startDate) + 1;
      if (totalDays > 365) {
        throw new Error('Maximum 365 days can be updated at once');
      }
      const dates = [];
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const formatted = format(currentDate, 'yyyy-MM-dd');
        const calDate = calendarDates?.find((d: CalendarDate) => d.date === formatted);
        if (!calDate || calDate.status === CalendarDateStatus.AVAILABLE) {
          dates.push({
            date: formatted,
            status: bulkStatus,
            price: bulkPrice ? parseInt(bulkPrice) : null,
            priceSource: useAiPricing ? CalendarPriceSource.AI : CalendarPriceSource.MANUAL,
          });
        }
        currentDate = addDays(currentDate, 1);
      }
      if (dates.length === 0) {
        throw new Error('No available days in the selected range to update.');
      }
      const response = await apiRequest(
        'POST',
        `properties/${selectedPropertyId}/calendar`,
        { dates }
      );
      return response;
    },
    onSuccess: () => {
      if (dateRange.from && dateRange.to) {
        toast({
          title: 'Calendar updated',
          description: `${differenceInDays(dateRange.to, dateRange.from) + 1} days have been updated`,
        });
      }
      setIsBulkModalOpen(false);
      setDateRange({ from: undefined, to: undefined });
      refetchCalendar();
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update calendar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  
  // Generate AI prices mutation
  const generateAiPricesMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPropertyId) {
        throw new Error('No property selected');
      }
      setIsGeneratingPrices(true);
      // Use selected range if set, else default to 1 year
      let startDate: Date, endDate: Date;
      if (dateRange.from && dateRange.to) {
        startDate = dateRange.from;
        endDate = dateRange.to;
      } else {
        startDate = new Date();
        endDate = addDays(startDate, 364); // 1 year (inclusive)
      }
      const response = await apiRequest(
        'POST',
        `properties/${selectedPropertyId}/generate-ai-pricing`,
        { 
          intensity: smartPricingIntensity,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
        }
      );
      const data = await response;
      
      // Handle the AI pricing response
      if (data.prices) {
        // Convert the prices object to an array of date objects
        let dates = Object.entries(data.prices).map(([date, price]) => ({
          date,
          status: CalendarDateStatus.AVAILABLE,
          price: Math.round(Number(price)),
          priceSource: CalendarPriceSource.AI,
        }));

        // Filter out dates that are not available in calendarDates
        if (calendarDates && Array.isArray(calendarDates)) {
          const availableSet = new Set(
            calendarDates
              .filter((d) => d.status === CalendarDateStatus.AVAILABLE)
              .map((d) => d.date)
          );
          dates = dates.filter((d) => availableSet.has(d.date));
        }
        
        // Update the calendar with the new prices
        await apiRequest(
          'POST',
          `properties/${selectedPropertyId}/calendar`,
          { dates }
        );

        // Optimistically update the calendar data
        queryClient.setQueryData(
          ['properties', selectedPropertyId, 'calendar'],
          (oldData: any) => {
            if (!oldData) return dates;
            
            // Create a map of existing dates for quick lookup
            const existingDatesMap = new Map(
              oldData.map((date: any) => [date.date, date])
            );
            
            // Update or add new dates
            dates.forEach((newDate) => {
              existingDatesMap.set(newDate.date, newDate);
            });
            
            // Convert map back to array
            return Array.from(existingDatesMap.values());
          }
        );
      }
      
      return data;
    },
    onSuccess: (data) => {
      setIsGeneratingPrices(false);
      
      if (!data.success) {
        toast({
          title: 'Smart Pricing Failed',
          description: data.message || 'Failed to generate AI prices',
          variant: 'destructive',
        });
        return;
      }
      
      toast({
        title: 'Smart Prices generated',
        description: 'AI prices have been generated successfully',
      });
      
      // Only refetch if optimistic update failed
      refetchCalendar();
    },
    onError: (error: Error) => {
      setIsGeneratingPrices(false);
      toast({
        title: 'Failed to generate AI prices',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  
  // Export calendar mutation
  const exportCalendarMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPropertyId) {
        throw new Error('No property selected');
      }
      
      const response = await apiRequest(
        'GET',
        `calendar/export/${selectedPropertyId}`,
      );
      console.log("response", response);

      // Set the export URL for the modal
        return `${import.meta.env.VITE_API_URL}/api/calendar/export/${selectedPropertyId}.ics`;
      },
    onSuccess: (url) => {
      console.log("url", url);
      setExportUrl(url);
      setIsExportModalOpen(true);
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to export calendar',
        description: error.message as any,
        variant: 'destructive',
      });
    },
  });
  
  // Export CSV mutation
  const exportCSVMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPropertyId || !csvStartDate || !csvEndDate) {
        throw new Error('Property and date range are required');
      }
      
      // Create query parameters
      const params = new URLSearchParams({
        startDate: format(csvStartDate, 'yyyy-MM-dd'),
        endDate: format(csvEndDate, 'yyyy-MM-dd'),
      });
      
      // Get the export URL
      const url = `properties/${selectedPropertyId}/export-csv?${params.toString()}`;
      
      // Create a link and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `calendar-export-${selectedPropertyId}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      return { success: true };
    },
    onSuccess: () => {
      setIsExportCSVModalOpen(false);
      setCsvStartDate(undefined);
      setCsvEndDate(undefined);
      toast({
        title: 'CSV Export started',
        description: 'Your pricing data is being downloaded',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to export CSV',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  
  // Handlers
  const handlePropertyChange = (propertyId: string) => {
    setSelectedPropertyId(parseInt(propertyId));
    // setSelect
    // Update the URL to /manager/calendar/{propertyId}
    navigate(`/manager/calendar/${propertyId}`);
  };
  
  const getCalendarDate = (day: Date) => {
    if (!calendarDates) return null;
    const formattedDate = format(day, 'yyyy-MM-dd');
    return calendarDates.find((date: CalendarDate) => date.date === formattedDate);
  };

  // Minimal custom Day renderer to show price (move inside function)
  const PriceDay = (props: any) => {
    const { date } = props;
    if (!date) return null;
    const calendarDate = getCalendarDate(date);
    const price = calendarDate?.price;
    const priceSource = calendarDate?.priceSource;
    const notes = calendarDate?.notes;
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span style={{ display: "block" }}>
              <div>{date.getDate()}</div>
              {price !== undefined && price !== null && (
                <div style={{ fontWeight: 'bold', fontSize: '0.9em', marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  {Math.round(price)}
                  {priceSource === PricingMode.AI ? (
                    <SparklesIcon className="h-3 w-3 text-amber-600 ml-1" />
                  ) : priceSource === PricingMode.MANUAL? (
                    <PencilIcon className="h-3 w-3 text-blue-600 ml-1" />
                  ) : null}
                </div>
              )}
            </span>
          </TooltipTrigger>
          {notes && (
            <TooltipContent side="top" align="center" className="py-2 px-3 text-xs font-medium max-w-[200px]">
              {notes}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  };
  
  const handleDateSelect = (day: Date | undefined) => {
    if (!day) return;
    setSelectedDate(day);
    setIsDateModalOpen(true);
  };
  
  const handleSyncIcal = () => {
    setIsIcalModalOpen(true);
  };
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied to clipboard',
      description: 'The export URL has been copied to your clipboard',
    });
  };
  
  // Helper to get source badge
  const getSourceBadge = (source: CalendarSource | CalendarPriceSource | string) => {
    // Manual sources
    if (source === CalendarPriceSource.MANUAL || source === 'manual') {
      return <Badge variant="outline" className="bg-blue-50 text-blue-800 hover:bg-blue-100">Manual</Badge>;
    }
    // iCal sources
    else if (source === CalendarSource.ICAL || source === 'ical') {
      return <Badge variant="outline" className="bg-purple-50 text-purple-800 hover:bg-purple-100">iCal</Badge>;
    }
    // Airbnb sources
    else if (source === CalendarSource.AIRBNB || source === 'airbnb') {
      return <Badge variant="outline" className="bg-pink-50 text-pink-800 hover:bg-pink-100">Airbnb</Badge>;
    }
    // Booking.com sources
    else if (source === CalendarSource.BOOKING || source === 'booking') {
      return <Badge variant="outline" className="bg-indigo-50 text-indigo-800 hover:bg-indigo-100">Booking.com</Badge>;
    }
    // AI sources
    else if (source === CalendarPriceSource.AI || source === 'ai') {
      return <Badge variant="outline" className="bg-amber-50 text-amber-800 hover:bg-amber-100">AI</Badge>;
    }
    // Imported sources
    else if (source === CalendarPriceSource.IMPORTED || source === 'imported') {
      return <Badge variant="outline" className="bg-green-50 text-green-800 hover:bg-green-100">Imported</Badge>;
    }
    // Default
    else {
      return <Badge variant="outline">{source}</Badge>;
    }
  };
  
  // Get price source icon
  const getPriceSourceIcon = (source?: CalendarSource | CalendarPriceSource | string) => {
    // If in manual pricing mode, always show manual icon
    if (!useSmartPricing) {
      return <PencilIcon className="h-3 w-3 text-blue-600" />;
    }
    
    // Handle AI source (enum or string)
    if (source === CalendarPriceSource.AI || source === 'ai') {
      return <SparklesIcon className="h-3 w-3 text-amber-600" />;
    } 
    // Handle imported sources (enum or string)
    else if (
      source === CalendarPriceSource.IMPORTED || 
      source === 'imported' ||
      source === CalendarSource.AIRBNB || 
      source === 'airbnb' ||
      source === CalendarSource.BOOKING || 
      source === 'booking' ||
      source === CalendarSource.ICAL || 
      source === 'ical'
    ) {
      return <CloudDownloadIcon className="h-3 w-3 text-purple-600" />;
    } 
    // Default to manual pricing
    else {
      return <PencilIcon className="h-3 w-3 text-blue-600" />;
    }
  };
  
  // On mount, if the URL contains a propertyId param, select that property
  useEffect(() => {
    const match = window.location.pathname.match(/\/manager\/calendar\/(\d+)/);
    if (match && match[1]) {
      setSelectedPropertyId(parseInt(match[1]));
    }
  }, []);
  
  // Add effect to log when dateRange changes
  useEffect(() => {
    console.log('[Calendar] dateRange state changed:', dateRange);
  }, [dateRange]);
  
  // 1. Helper to get lighter status class for outside days
  function getOutsideDayClass(date: Date) {
    const calendarDate = getCalendarDate(date);
    if (!calendarDate) return "bg-gray-100 text-gray-400 border border-gray-200";
    switch (calendarDate.status) {
      case CalendarDateStatus.BOOKED:
        return "bg-red-50 text-red-300 border border-red-100";
      case CalendarDateStatus.BLOCKED:
        return "bg-gray-300 text-gray-500 border border-gray-200";
      case CalendarDateStatus.AVAILABLE:
        return "bg-green-50 text-green-300 border border-green-100";
      default:
        return "bg-gray-100 text-gray-400 border border-gray-200";
    }
  }
  
  // Helper for min/max date
  const today = new Date();
  const maxDate = addDays(today, 364);
  
  return (
    <ManagerLayout>
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Property Calendar</h1>
            <p className="text-muted-foreground mt-1">Manage your property availability and pricing</p>
          </div>
          <div className="flex gap-2 items-center">
            {selectedProperty && selectedProperty.airbnbListingId && (
              <Button
                variant="default"
                className="font-semibold"
                onClick={async () => {
                  try {
                    setIsGeneratingPrices(true);
                    const response = await apiRequest('POST', `properties/${selectedProperty.id}/sync-airbnb-calendar`, {
                      airbnbListingId: selectedProperty.airbnbListingId
                    });
                    toast({
                      title: 'Calendar Synced',
                      description: response.message || 'Airbnb calendar has been synced and updated.',
                    });
                    refetchCalendar();
                  } catch (error) {
                    toast({
                      title: 'Sync Failed',
                      description: (error as any).message || 'Failed to sync with Airbnb calendar',
                      variant: 'destructive',
                    });
                  } finally {
                    setIsGeneratingPrices(false);
                  }
                }}
                disabled={isGeneratingPrices}
              >
                {isGeneratingPrices ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Sync with Airbnb
              </Button>
            )}
              <Button disabled={true}
                variant="default"
                className="font-semibold" >
                  Sync with Hostaway
              </Button>
              <Button disabled={true}
                variant="default"
                className="font-semibold" >
                  Sync with Pricelabs
              </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="font-semibold">
                  <Settings className="w-4 h-4 mr-2" />
                  Export / Import
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportCalendarMutation.mutate()} disabled={!selectedPropertyId}>
                  <ArrowUp className="mr-2 h-4 w-4" /> Export Calendar (iCal)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsExportCSVModalOpen(true)} disabled={!selectedPropertyId}>
                  <FileDown className="mr-2 h-4 w-4" /> Export Pricing (CSV)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSyncIcal} disabled={!selectedPropertyId}>
                  <ArrowDown className="mr-2 h-4 w-4" /> Import Calendar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSyncIcal} disabled={true}>
                  <ArrowDown className="mr-2 h-4 w-4" /> Import Pricing (CSV)
                </DropdownMenuItem>

              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        <div className="grid gap-6 grid-cols-1 md:grid-cols-12">
          {/* Left sidebar */}
          <div className="md:col-span-3">
            <Card>
              { <CardHeader>
                <CardTitle>Select Property</CardTitle>
                {/* <CardDescription>
                  Manage your property calendar
                </CardDescription>  */}
              </CardHeader> }
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  {/* <Label htmlFor="property">Select Property</Label> */}
                  <Select
                    value={selectedPropertyId ? selectedPropertyId.toString() : undefined}
                    onValueChange={handlePropertyChange}
                    disabled={propertiesLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a property" />
                    </SelectTrigger>
                    <SelectContent>
                      {propertiesLoading ? (
                        <div className="flex items-center justify-center p-4">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : properties && properties.length > 0 ? (
                        properties.map((property: Property) => (
                          <SelectItem key={property.id} value={property.id.toString()}>
                            {property.title}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-gray-500">No properties found</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="pt-4 space-y-3">
                  <h3 className="text-sm font-medium">Calendar Actions</h3>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="range-selection" className="flex items-center">
                      <span className="text-sm">Date Range Selection</span>
                    </Label>
                    <Switch 
                      id="range-selection" 
                      checked={isRangeSelectionMode}
                      onCheckedChange={setIsRangeSelectionMode}
                    />
                  </div>
                  
                   
                  
                  {isRangeSelectionMode && (
                    <div className="mt-2 flex flex-col gap-1">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex items-center border rounded-md px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary w-fit"
                          >
                            <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                            {dateRange.from && dateRange.to ? (
                              <span>
                                {format(dateRange.from, 'MMM d, yyyy')} — {format(dateRange.to, 'MMM d, yyyy')}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Select date range</span>
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-4" align="start">
                          <div className="flex gap-4">
                            <div>
                              <Label>Start Date</Label>
                              <Calendar
                                mode="single"
                                selected={dateRange.from}
                                onSelect={date => {
                                  setDateRange(r => ({ ...r, from: date }));
                                }}
                                className="border rounded-md p-2"
                                disabled={date => !!(date < today || date > maxDate)}
                                classNames={{
                                  day_selected: "bg-yellow-300 text-black font-bold rounded-md border border-yellow-400",
                                  day: "h-10 w-10 p-0 font-normal aria-selected:opacity-100 rounded-md",
                                }}
                              />
                            </div>
                            <div>
                              <Label>End Date</Label>
                              <Calendar
                                mode="single"
                                selected={dateRange.to}
                                onSelect={date => {
                                  setDateRange(r => ({ ...r, to: date }));
                                }}
                                className="border rounded-md p-2"
                                disabled={date => !!(date < today || date > maxDate || (dateRange.from && date < dateRange.from))}
                                classNames={{
                                  day_selected: "bg-yellow-300 text-black font-bold rounded-md border border-yellow-400",
                                  day: "h-10 w-10 p-0 font-normal aria-selected:opacity-100 rounded-md",
                                }}
                              />
                            </div>
                          </div>
                          <Button size="sm" variant="outline" className="mt-3 w-fit" onClick={() => setDateRange({ from: undefined, to: undefined })}>
                            Clear Selection
                          </Button>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>
                
                {selectedPropertyId && (
                  <div className="pt-4 space-y-3">
                    <h3 className="text-sm font-medium">Pricing Mode</h3>
                    {(propertiesLoading || !selectedProperty) ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        <span>Loading property details...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4">
                       
                        {/* Smart AI Pricing Card */}
                        <div
                          className={`flex-1 border rounded-lg p-4 cursor-pointer transition-all ${useSmartPricing ? 'border-primary ring-2 ring-primary' : 'border-muted'}`}
                          onClick={() => {
                            setUseSmartPricing(true);
                          }}
                          tabIndex={0}
                          role="button"
                        >
                          <div className="flex items-center mb-2">
                            <SparklesIcon className="mr-2 h-5 w-5 text-primary" />
                            <span className="font-semibold text-lg">Smart AI Pricing</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">Let AI optimize your property pricing based on market trends, location, and seasonal demand.</p>
                          {useSmartPricing && (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="intensity" className="text-sm">Pricing Intensity</Label>
                                <Select
                                  value={smartPricingIntensity}
                                  onValueChange={(value) => setSmartPricingIntensity(value as SmartPricingIntensity)}
                                >
                                  <SelectTrigger id="intensity">
                                    <SelectValue placeholder="Select intensity" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={SmartPricingIntensity.MILD}>Mild</SelectItem>
                                    <SelectItem value={SmartPricingIntensity.MEDIUM}>Medium</SelectItem>
                                    <SelectItem value={SmartPricingIntensity.STRONG}>Strong</SelectItem>
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                  Higher intensity means more aggressive price adjustments
                                </p>
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="w-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowAiPriceConfirm(true);
                                }}
                                disabled={generateAiPricesMutation.isPending || isGeneratingPrices}
                              >
                                {generateAiPricesMutation.isPending || isGeneratingPrices ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <SparklesIcon className="mr-2 h-4 w-4" />
                                )}
                                Generate AI Prices
                              </Button>
                            </>
                          )}
                        </div>
                         {/* Manual Pricing Card */}
                        <div
                          className={`flex-1 border rounded-lg p-4 cursor-pointer transition-all ${!useSmartPricing ? 'border-primary ring-2 ring-primary' : 'border-muted'}`}
                          onClick={() => {
                            setUseSmartPricing(false);
                          }}
                          tabIndex={0}
                          role="button"
                        >
                          <div className="flex items-center mb-2">
                            <DollarSign className="text-muted-foreground mr-2 h-5 w-5" />
                            <span className="font-semibold text-lg">Manual Pricing</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">Set your own base price for this property.</p>
                          {!useSmartPricing && (
                            <div className="flex flex-col gap-2 mt-2">
                              <Input
                                id="basePrice"
                                type="number"
                                min={0}
                                value={selectedProperty?.basePrice || ''}
                                onChange={(e) => {
                                  const newBasePrice = parseInt(e.target.value);
                                  if (selectedProperty) {
                                    setSelectedProperty({
                                      ...selectedProperty,
                                      basePrice: newBasePrice
                                    });
                                  }
                                }}
                                placeholder="Enter base price"
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowManualPriceConfirm(true);
                                }}
                              >
                                Apply to all Dates
                              </Button>
                              {selectedProperty?.basePrice && (
                                <div className="text-xs text-muted-foreground">
                                  Current base price: {selectedProperty.basePrice} AED
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
              </CardContent>
            </Card>
          </div>
          
          {/* Calendar */}
          <div className="md:col-span-9">
            <Card className="h-full">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <CardTitle>
                      {selectedProperty ? selectedProperty.title : 'Property Calendar'}
                    </CardTitle>
                    {selectedProperty && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          asChild
                          title="View Property"
                        >
                          <a
                            href={`/properties/${selectedProperty.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Eye className="w-5 h-5" />
                          </a>
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          asChild
                          title="Edit Property"
                        >
                          <a
                            href={`/manager/edit-property/${selectedProperty.id}`}
                            rel="noopener noreferrer"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </a>
                        </Button>
                      </>
                    )}
                  </div>
                 
                  {selectedProperty && (
                    <div className="flex flex-col items-center space-y-1 ml-4">
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="bg-blue-50 text-blue-800">
                          Base Price: {selectedProperty.basePrice?.toLocaleString()} AED
                        </Badge>
                        <Badge variant="outline" className="bg-blue-50 text-blue-800">
                          {selectedProperty.area}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant="outline" className="bg-blue-50 text-blue-800">
                          {selectedProperty.bedrooms === 0 ? "Studio" : `${selectedProperty.bedrooms} Bedroom`}
                        </Badge>
                        <Badge variant="outline" className="bg-blue-50 text-blue-800">
                          {selectedProperty.bathrooms} Bathroom
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {propertiesLoading || !selectedProperty ? (
                  <div className="flex flex-col items-center justify-center h-[500px]">
                    <Loader2 className="h-8 w-8 animate-spin mb-4" />
                    <p className="text-muted-foreground">Loading property details...</p>
                  </div>
                ) : selectedPropertyId ? (
                  <div className="overflow-x-auto">
                    {isRangeSelectionMode ? (
                      <>
                        <Calendar
                          mode="range"
                          selected={dateRange}
                          onSelect={(range: DateRange | undefined) => {
                            if (range) {
                              let from = range.from;
                              let to = range.to;
                              if (from && to && to < from) {
                                // Swap if end is before start
                                [from, to] = [to, from];
                              }
                              setDateRange({ from, to });
                              if (from && to) {
                                setBulkStatus(CalendarDateStatus.AVAILABLE);
                                setBulkPrice('');
                                setUseAiPricing(false);
                                setIsBulkModalOpen(true);
                              }
                            }
                          }}
                          className="rounded-md border p-4"
                          numberOfMonths={1}
                          showOutsideDays={true}
                          disabled={(date: Date) => {
                            // If start date is selected, only allow future dates
                            if (dateRange?.from && !dateRange?.to) {
                              return date < dateRange.from;
                            }
                            // Otherwise, only disable past dates
                            return date < new Date(new Date().setHours(0, 0, 0, 0));
                          }}
                          modifiers={{
                            booked: (date: Date) => {
                              const calendarDate = getCalendarDate(date);
                              return calendarDate?.status === CalendarDateStatus.BOOKED;
                            },
                            blocked: (date: Date) => {
                              const calendarDate = getCalendarDate(date);
                              return calendarDate?.status === CalendarDateStatus.BLOCKED;
                            },
                            available: (date: Date) => {
                              const calendarDate = getCalendarDate(date);
                              return calendarDate?.status === CalendarDateStatus.AVAILABLE;
                            },
                          }}
                          modifiersClassNames={{
                            booked: "bg-red-200 text-red-700 border border-red-300 font-semibold",
                            blocked: "bg-gray-700 text-white border border-gray-700 font-semibold",
                            available: "bg-green-100 text-green-700 border border-green-300",
                            past: "bg-gray-200 text-gray-400 border border-gray-300",
                            selected: "bg-yellow-300 text-black font-bold border border-yellow-400",
                            disabled: "bg-gray-200 text-gray-400 border border-gray-300",
                            outside: "bg-gray-100 text-gray-400 border border-gray-200 rounded-md"
                          }}
                          classNames={{
                            months: "flex flex-col md:flex-row space-y-8 md:space-y-0 md:space-x-8",
                            month: "space-y-6",
                            caption: "flex justify-center relative items-center px-2 pb-4",
                            caption_label: "text-sm font-medium",
                            nav: "space-x-1 flex items-center",
                            nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                            nav_button_previous: "absolute left-1",
                            nav_button_next: "absolute right-1",
                            table: "w-full border-collapse space-y-2",
                            head_row: "flex justify-between px-1",
                            head_cell: "w-9 font-normal text-[0.8rem] text-center",
                            row: "flex w-full mt-3 justify-between",
                            cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md mx-0.5",
                            day: "h-16 w-16 p-0 font-normal aria-selected:opacity-100 rounded-md",
                            day_range_end: "bg-yellow-300 text-black font-bold rounded-md border border-yellow-400",
                            day_selected: "bg-yellow-300 text-black font-bold rounded-md border border-yellow-400",
                            day_today: "border-2 border-black bg-accent text-accent-foreground",
                            day_outside: "bg-gray-100 text-gray-400 border border-gray-200 rounded-md",
                            day_disabled: "bg-gray-200 text-gray-400 border border-gray-300 rounded-md opacity-50",
                            day_range_middle: "bg-green-100 text-green-900 hover:bg-green-200",
                            day_hidden: "invisible"
                          }}
                          components={{ DayContent: PriceDay as any }}
                        />
                        <div className="mt-2 flex flex-col gap-1">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4 mr-1" />
                            Selected Start Date: {dateRange.from ? <span className="font-semibold">{format(dateRange.from, 'MMM d, yyyy')}</span> : <span className="italic">None</span>}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4 mr-1" />
                            Selected End Date: {dateRange.to ? <span className="font-semibold">{format(dateRange.to, 'MMM d, yyyy')}</span> : <span className="italic">None</span>}
                          </span>
                          <Button size="sm" variant="outline" className="mt-1 w-fit" onClick={() => setDateRange({ from: undefined, to: undefined })}>
                            Clear Selection
                          </Button>
                        </div>
                      </>
                    ) : (
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateSelect}
                        className="rounded-md border p-4"
                        numberOfMonths={1}
                        showOutsideDays={true}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        modifiers={{
                          booked: (date) => {
                            const calendarDate = getCalendarDate(date);
                            return calendarDate?.status === CalendarDateStatus.BOOKED;
                          },
                          blocked: (date) => {
                            const calendarDate = getCalendarDate(date);
                            return calendarDate?.status === CalendarDateStatus.BLOCKED;
                          },
                          available: (date) => {
                            const calendarDate = getCalendarDate(date);
                            return calendarDate?.status === CalendarDateStatus.AVAILABLE;
                          },
                        }}
                        modifiersClassNames={{
                          booked: "bg-red-200 text-red-700 border border-red-300 font-semibold",
                          blocked: "bg-gray-700 text-white border border-gray-700 font-semibold",
                          available: "bg-green-100 text-green-700 border border-green-300",
                          past: "bg-gray-200 text-gray-400 border border-gray-300",
                          selected: "bg-yellow-300 text-black font-bold border border-yellow-400",
                          disabled: "bg-gray-200 text-gray-400 border border-gray-300",
                          outside: "bg-gray-100 text-gray-400 border border-gray-200 rounded-md"
                        }}
                        classNames={{
                          months: "flex flex-col md:flex-row space-y-8 md:space-y-0 md:space-x-8",
                          month: "space-y-6",
                          caption: "flex justify-center relative items-center px-2 pb-4",
                          caption_label: "text-sm font-medium",
                          nav: "space-x-1 flex items-center",
                          nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                          nav_button_previous: "absolute left-1",
                          nav_button_next: "absolute right-1",
                          table: "w-full border-collapse space-y-2",
                          head_row: "flex justify-between px-1",
                          head_cell: "w-9 font-normal text-[0.8rem] text-center",
                          row: "flex w-full mt-3 justify-between",
                          cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md mx-0.5",
                          day: "h-16 w-16 p-0 font-normal aria-selected:opacity-100 rounded-md",
                          day_range_end: "bg-yellow-300 text-black font-bold rounded-md border border-yellow-400",
                          day_selected: "bg-yellow-300 text-black font-bold rounded-md border border-yellow-400",
                          day_today: "border-2 border-black bg-accent text-accent-foreground",
                          day_outside: "bg-gray-100 text-gray-400 border border-gray-200 rounded-md",
                          day_disabled: "bg-gray-200 text-gray-400 border border-gray-300 rounded-md opacity-50",
                          day_range_middle: "bg-green-100 text-green-900 hover:bg-green-200",
                          day_hidden: "invisible"
                        }}
                        components={{ DayContent: PriceDay as any }}
                      />
                    )}

                    {/* Calendar Legend and Price Sources */}
                    <div className="mt-6 grid grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <h3 className="text-sm font-medium">Calendar Legend</h3>
                        <div className="grid grid-cols-1 gap-2">
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded bg-green-100 mr-2 border border-green-300"></div>
                            <span className="text-sm">Available</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded bg-red-200 mr-2 border border-red-300"></div>
                            <span className="text-sm">Booked</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded bg-gray-700 mr-2 border border-gray-700"></div>
                            <span className="text-sm text-black">Blocked</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded bg-gray-200 mr-2 border border-gray-300"></div>
                            <span className="text-sm">Past/Future</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded bg-yellow-300 mr-2 border border-yellow-400"></div>
                            <span className="text-sm">Selected</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <h3 className="text-sm font-medium">Price Sources</h3>
                        <div className="grid grid-cols-1 gap-2">
                          <div className="flex items-center">
                            <PencilIcon className="h-4 w-4 text-blue-600 mr-2" />
                            <span className="text-sm">Manual Pricing</span>
                          </div>
                          <div className="flex items-center">
                            <SparklesIcon className="h-4 w-4 text-amber-600 mr-2" />
                            <span className="text-sm">AI Pricing</span>
                          </div>
                          <div className="flex items-center">
                            <CloudDownloadIcon className="h-4 w-4 text-purple-600 mr-2" />
                            <span className="text-sm">Imported Pricing</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[500px] text-center">
                    <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium">No property selected</h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Please select a property from the dropdown to view and manage its availability calendar
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* iCal Import Modal */}
        <Dialog open={isIcalModalOpen} onOpenChange={setIsIcalModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Import Calendar</DialogTitle>
              <DialogDescription>
                Connect your property calendar to external platforms
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Existing imports */}
              {!importsLoading && calendarImports && calendarImports.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium">Existing Calendar Imports</h3>
                  <div className="border rounded-md divide-y">
                    {calendarImports.map((calImport: any) => (
                      <div key={calImport.id} className="p-3 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {calImport.source === CalendarSource.AIRBNB && (
                            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-pink-100">
                              <CloudDownloadIcon className="h-4 w-4 text-pink-600" />
                            </div>
                          )}
                          {calImport.source === CalendarSource.BOOKING && (
                            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-100">
                              <CloudDownloadIcon className="h-4 w-4 text-blue-600" />
                            </div>
                          )}
                          {calImport.source === CalendarSource.ICAL && (
                            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-100">
                              <CloudDownloadIcon className="h-4 w-4 text-purple-600" />
                            </div>
                          )}
                          {(calImport.source === CalendarSource.GUESTY || calImport.source === CalendarSource.HOSTAWAY) && (
                            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100">
                              <CloudDownloadIcon className="h-4 w-4 text-gray-600" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-sm">
                              {calImport.label || `${calImport.source} Calendar`}
                            </div>
                            <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                              {calImport.url}
                            </div>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteImportMutation.mutate(calImport.id)}
                          disabled={deleteImportMutation.isPending}
                        >
                          {deleteImportMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <XCircle className="h-4 w-4 text-rose-500" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            
              <div className="pt-2 border-t">
                <h3 className="text-sm font-medium mb-3">Add New Calendar Import</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="icalSource">Calendar Source</Label>
                    <Select
                      value={icalSource}
                      onValueChange={(value) => setIcalSource(value as CalendarSource)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={CalendarSource.ICAL}>Custom iCal</SelectItem>
                        <SelectItem value={CalendarSource.AIRBNB}>Airbnb</SelectItem>
                        <SelectItem value={CalendarSource.BOOKING}>Booking.com</SelectItem>
                        <SelectItem value={CalendarSource.GUESTY}>Guesty</SelectItem>
                        <SelectItem value={CalendarSource.HOSTAWAY}>Hostaway</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="icalLabel">Calendar Label (Optional)</Label>
                    <Input
                      id="icalLabel"
                      placeholder="e.g., Airbnb Main Calendar, Booking.com Backup"
                      value={icalLabel}
                      onChange={(e) => setIcalLabel(e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="icalUrl">Calendar URL</Label>
                    <Input
                      id="icalUrl"
                      placeholder="https://platform.com/calendar/ical/12345.ics"
                      value={icalUrl}
                      onChange={(e) => setIcalUrl(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Paste the iCal URL from your external platform. This will import all bookings into your calendar.
                    </p>
                  </div>
                
                  <Alert>
                    <InfoIcon className="h-4 w-4" />
                    <AlertTitle>How to find your Calendar URL</AlertTitle>
                    <AlertDescription className="text-xs">
                      <ul className="list-disc pl-4 space-y-1 mt-2">
                        <li>Airbnb: Go to Listings &gt; Calendar &gt; Availability settings &gt; Sync calendars</li>
                        <li>Booking.com: Go to Property &gt; Calendar &gt; Calendar sync</li>
                        <li>Other platforms: Look for Calendar Sync, iCal Export, or similar options</li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsIcalModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                disabled={!icalUrl || syncIcalMutation.isPending} 
                onClick={() => syncIcalMutation.mutate()}
              >
                {syncIcalMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowDown className="mr-2 h-4 w-4" />
                )}
                Import Calendar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Date Edit Modal */}
        <Dialog open={isDateModalOpen} onOpenChange={setIsDateModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                Edit Date: {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : ''}
              </DialogTitle>
              <DialogDescription>
                Update availability and pricing for this date
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="status">Availability Status</Label>
                <Select
                  value={dateStatus}
                  onValueChange={(value) => setDateStatus(value as CalendarDateStatus)}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CalendarDateStatus.AVAILABLE}>Available</SelectItem>
                    <SelectItem value={CalendarDateStatus.BOOKED}>Booked</SelectItem>
                    <SelectItem value={CalendarDateStatus.BLOCKED}>Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {dateStatus === CalendarDateStatus.AVAILABLE && (
                <div className="space-y-2">
                  <Label htmlFor="price">Price (AED)</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    placeholder="Enter price"
                    value={datePrice}
                    onChange={(e) => setDatePrice(e.target.value)}
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add notes about this date"
                  value={dateNotes}
                  onChange={(e) => setDateNotes(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDateModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => updateDateMutation.mutate()} 
                disabled={updateDateMutation.isPending}
              >
                {updateDateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Bulk Edit Modal */}
        <Dialog open={isBulkModalOpen} onOpenChange={setIsBulkModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Bulk Update Dates</DialogTitle>
              <DialogDescription>
                Update {dateRange.from && dateRange.to ? differenceInDays(dateRange.to, dateRange.from) + 1 : 0} days from {dateRange.from ? format(dateRange.from, 'MMM d, yyyy') : ''} to {dateRange.to ? format(dateRange.to, 'MMM d, yyyy') : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bulkStatus">Availability Status</Label>
                <Select
                  value={bulkStatus}
                  onValueChange={(value) => setBulkStatus(value as CalendarDateStatus)}
                >
                  <SelectTrigger id="bulkStatus">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CalendarDateStatus.AVAILABLE}>Available</SelectItem>
                    <SelectItem value={CalendarDateStatus.BOOKED}>Booked</SelectItem>
                    <SelectItem value={CalendarDateStatus.BLOCKED}>Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {bulkStatus === CalendarDateStatus.AVAILABLE && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="bulkPrice">Price (AED)</Label>
                    <Input
                      id="bulkPrice"
                      type="number"
                      min="0"
                      placeholder="Enter price for all selected dates"
                      value={bulkPrice}
                      onChange={(e) => setBulkPrice(e.target.value)}
                    />
                  </div>
                  
                  {selectedProperty?.useSmartPricing && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="aiPricing"
                        checked={useAiPricing}
                        onCheckedChange={(checked: boolean) => setUseAiPricing(checked)}
                      />
                      <Label htmlFor="aiPricing" className="text-sm font-normal">
                        Use AI pricing for these dates
                      </Label>
                    </div>
                  )}
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsBulkModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => bulkUpdateMutation.mutate()} 
                disabled={bulkUpdateMutation.isPending}
              >
                {bulkUpdateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                Update All Dates
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Export Calendar Modal */}
        <Dialog open={isExportModalOpen} onOpenChange={setIsExportModalOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Export Calendar</DialogTitle>
              <DialogDescription>
                Use this link to sync your StayMiddle calendar with other platforms
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="exportUrl">Calendar URL</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="exportUrl"
                    readOnly
                    value={exportUrl}
                  />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => copyToClipboard(exportUrl)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    asChild
                    title="Download .ics"
                    disabled={!exportUrl}
                  >
                    <a
                      href={exportUrl}
                      download={`calendar-export.ics`}
                      target="_self"
                      rel="noopener noreferrer"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Import this URL into other platforms to keep calendars in sync
                </p>
              </div>
              
              <Alert>
                <InfoIcon className="h-4 w-4" />
                <AlertTitle>How to use this export URL</AlertTitle>
                <AlertDescription className="text-xs">
                  <ul className="list-disc pl-4 space-y-1 mt-2">
                    <li>Airbnb: Listings &gt; Calendar &gt; Availability settings &gt; Sync calendars &gt; Import calendar</li>
                    <li>Booking.com: Extranet &gt; Rates & Availability &gt; Calendar &gt; Sync calendars &gt; Import</li>
                    <li>Other platforms: Look for Calendar Import, iCal Import, or similar options</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <Button onClick={() => setIsExportModalOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Export CSV Modal */}
        <Dialog open={isExportCSVModalOpen} onOpenChange={setIsExportCSVModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Export Pricing Data (CSV)</DialogTitle>
              <DialogDescription>
                Select a date range to export pricing data
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Calendar
                    mode="single"
                    selected={csvStartDate}
                    onSelect={setCsvStartDate}
                    className="border rounded-md p-3"
                    classNames={{
                      months: "space-y-4",
                      month: "space-y-4",
                      caption: "flex justify-center relative items-center px-2 pb-2",
                      caption_label: "text-sm font-medium",
                      table: "w-full border-collapse space-y-1",
                      head_row: "flex justify-between px-1",
                      head_cell: "w-8 font-normal text-[0.8rem] text-center",
                      row: "flex w-full mt-2 justify-between",
                      cell: "relative p-0 text-center text-sm mx-0.5",
                      day: "h-8 w-8 p-0 font-normal rounded-md",
                      day_today: "border border-black bg-accent text-accent-foreground",
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Calendar
                    mode="single"
                    selected={csvEndDate}
                    onSelect={setCsvEndDate}
                    disabled={(date) => csvStartDate ? date < csvStartDate : false}
                    className="border rounded-md p-3"
                    classNames={{
                      months: "space-y-4",
                      month: "space-y-4",
                      caption: "flex justify-center relative items-center px-2 pb-2",
                      caption_label: "text-sm font-medium",
                      table: "w-full border-collapse space-y-1",
                      head_row: "flex justify-between px-1",
                      head_cell: "w-8 font-normal text-[0.8rem] text-center",
                      row: "flex w-full mt-2 justify-between",
                      cell: "relative p-0 text-center text-sm mx-0.5",
                      day: "h-8 w-8 p-0 font-normal rounded-md",
                      day_today: "border border-black bg-accent text-accent-foreground",
                    }}
                  />
                </div>
              </div>
              
              <Alert>
                <InfoIcon className="h-4 w-4" />
                <AlertTitle>About the CSV Export</AlertTitle>
                <AlertDescription className="text-xs">
                  The CSV will contain dates, prices, availability status, and pricing source for the selected date range.
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsExportCSVModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => exportCSVMutation.mutate()} 
                disabled={exportCSVMutation.isPending || !csvStartDate || !csvEndDate}
              >
                {exportCSVMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="mr-2 h-4 w-4" />
                )}
                Export CSV
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Confirmation Dialog for Generate AI Prices */}
        <Dialog open={showAiPriceConfirm} onOpenChange={setShowAiPriceConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Generate AI Prices</DialogTitle>
              <DialogDescription>
                {dateRange.from && dateRange.to ? (
                  <>
                    This will <b>replace all existing prices</b> of <b>available dates</b> from <b>{format(dateRange.from, 'MMM d, yyyy')}</b> to <b>{format(dateRange.to, 'MMM d, yyyy')}</b> ({differenceInDays(dateRange.to, dateRange.from) + 1} days).
                  </>
                ) : (
                  <>
                    This will <b>replace all existing prices</b> of <b>available dates</b> for the next 1 year (365 days) from today.
                  </>
                )}
                <br/><br/>
                Booked and blocked dates will not be changed.<br/><br/>
                Are you sure you want to continue?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAiPriceConfirm(false)}>
                Cancel
              </Button>
              <Button 
                onClick={async () => {
                  setShowAiPriceConfirm(false);
                  const result = await generateAiPricesMutation.mutateAsync();
                  if (result && result.success) {
                    setUseSmartPricing(true);
                  }
                }}
                disabled={generateAiPricesMutation.isPending || isGeneratingPrices}
              >
                {generateAiPricesMutation.isPending || isGeneratingPrices ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <SparklesIcon className="mr-2 h-4 w-4" />
                )}
                Yes, Generate AI Prices
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Confirmation Dialog for Manual Pricing */}
        <Dialog open={showManualPriceConfirm} onOpenChange={setShowManualPriceConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Apply Manual Price</DialogTitle>
              <DialogDescription>
                This will <b>replace all existing prices</b> of <b>available dates</b> for the next 1 year (365 days) from today with <b>{selectedProperty?.basePrice} AED</b>.<br/><br/>
                Booked and blocked dates will not be changed.<br/><br/>
                Are you sure you want to continue?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowManualPriceConfirm(false)}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  setIsApplyingManualPrice(true);
                  try {
                    setShowManualPriceConfirm(false);
                    if (!selectedPropertyId || !selectedProperty?.basePrice) return;

                    // Confirm action
                    const startDate = new Date();
                    const endDate = new Date();
                    endDate.setDate(startDate.getDate() + 364);
                    // Fetch calendar dates for the year (if not already loaded)
                    let allDates = [];
                    try {
                      const data = await apiRequest(
                        'GET',
                        `properties/${selectedPropertyId}/calendar?startDate=${format(startDate, 'yyyy-MM-dd')}&endDate=${format(endDate, 'yyyy-MM-dd')}`
                      );
                      allDates = data.data || [];
                    } catch (err) {
                      // fallback: use current calendarDates
                      allDates = calendarDates || [];
                    }
                    // Filter available dates
                    const availableDates = allDates.filter((d: any) => d.status === 'available');
                    if (availableDates.length === 0) {
                      toast({ title: 'No available dates', description: 'No available dates found for the next year.' });
                      return;
                    }
                    // Prepare bulk update payload
                    const dates = availableDates.map((d: any) => ({
                      date: d.date,
                      status: CalendarDateStatus.AVAILABLE,
                      price: selectedProperty.basePrice,
                      priceSource: CalendarSource.MANUAL,
                    }));
                    try {
                      await apiRequest('POST', `properties/${selectedPropertyId}/calendar`, { dates });
                      toast({ title: 'Manual prices applied', description: `Base price applied to ${dates.length} available dates for 1 year.` });
                      refetchCalendar();
                    } catch (err: any) {
                      toast({ title: 'Failed to apply manual prices', description: err.message, variant: 'destructive' });
                    }
                  } catch (err: any) {
                    toast({ title: 'Failed to apply manual prices', description: err.message, variant: 'destructive' });
                  } finally {
                    setIsApplyingManualPrice(false);
                  }
                }}
                disabled={isApplyingManualPrice}
              >
                {isApplyingManualPrice ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DollarSign className="mr-2 h-4 w-4" />}
                Yes, Apply Manual Price
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ManagerLayout>
  );
}